<script setup lang="ts">
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import PassRateReportForm from '@/components/Reports/PassRate/PassRateReportForm.vue';
import PassRateReportOverall from '@/components/Reports/PassRate/PassRateReportOverall.vue';
import type { PassRateReport } from '@/types/PassRateReport';
import { useSchoolStore } from '@/stores/school';
import CoursePassRateCard from '@/components/Reports/PassRate/CoursePassRateCard.vue';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';

const $q = useQuasar();
const loading = ref<boolean>(false);
const submittedStartDate = ref<string>('');
const submittedEndDate = ref<string>('');

const passRateReport = ref<PassRateReport|null>(null);
const { school } = useSchoolStore();

// Form validation is handled in the child component
const submitForm = async (startDate: string, endDate: string) => {
    if (school) {
        loading.value = true;
        submittedStartDate.value = startDate;
        submittedEndDate.value = endDate;

        passRateReport.value = await FlightSchoolRepository.getPassRateReport(school, startDate, endDate).catch(
            (error) => {
                $q.notify({
                    message: 'An error occurred while fetching the pass rate report',
                    color: 'red-5',
                    textColor: 'white',
                    icon: 'warning',
                });
                loading.value = false;
                throw error;
            },
        );
        loading.value = false;
    }
};
</script>
<template>
    <div class="row">
        <div class="col-12 text-center q-my-md header">
            <h3
                class="title q-mb-md text-center fsum-generic-header"
                tabindex="0"
                role="heading"
                aria-level="3"
            >
                FAA Knowledge Test Pass Rate
            </h3>
            <p
                class="subtitle print-hide"
                tabindex="0"
                role="heading"
                aria-level="4"
            >
                Please select a date range below to calculate the FAA Knowledge Test pass rate for each of your courses.
            </p>
        </div>
    </div>
    <div
        class="pass-rate-content"
        aria-live="polite"
    >
        <PassRateReportForm
            :loading="loading"
            @submit:form="(startDay: string, endDay: string) => submitForm(startDay, endDay)"
        />
        <template v-if="passRateReport && school">
            <PassRateReportOverall
                :school="school"
                :pass-rate-report="passRateReport"
                :start-date="submittedStartDate"
                :end-date="submittedEndDate"
            />
            <div
                class="course-cards q-gutter-y-lg q-mt-sm q-mx-sm row"
                :class="passRateReport.courseRates.length === 3 ? 'justify-around' : 'justify-between'"
            >
                <CoursePassRateCard
                    v-for="(coursePassRate, index) in passRateReport.courseRates"
                    :key="index"
                    :course-pass-rate="coursePassRate"
                    :use-full-width="passRateReport.courseRates.length === 1"
                />
            </div>
        </template>
    </div>
</template>
<style scoped lang="scss">
div.header {
    color: var(--fsum-dark-blue2);

    p.subtitle {
        font-size: 18px;
        text-align: center;
    }
}
.pass-rate-content {
    margin: auto;
    max-width: 900px;
    font-size: 16px;
}
</style>
